import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import DOMPurify from 'dompurify';
import { useConfluencePage } from './useConfluencePage';

interface ConfluencePageViewProps {
  pageId: string | null;
  emptyMessage?: string;
}

export const ConfluencePageView: React.FC<ConfluencePageViewProps> = ({
  pageId,
  emptyMessage = 'Select a page to view its content.',
}) => {
  const { page, loading, error } = useConfluencePage(pageId);

  const sanitizedHtml = useMemo(() => {
    if (!page?.html) return '';
    return DOMPurify.sanitize(page.html, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
    });
  }, [page?.html]);

  if (!pageId) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'text.secondary', border: '2px dashed #e5e7eb', borderRadius: 2, m: 3 }}>
        <ArticleIcon sx={{ fontSize: 40, opacity: 0.3 }} />
        <Typography variant="body2">{emptyMessage}</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={32} sx={{ color: '#1a202c' }} />
      </Box>
    );
  }

  if (error) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  }

  if (!page) return null;

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
      {/* Page title */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: '#1a202c', mb: 3, pb: 2, borderBottom: '1px solid #e5e7eb' }}
      >
        {page.title}
      </Typography>

      {/* Rendered HTML with Confluence-like styles */}
      <Box
        sx={{
          // Typography
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: '14px',
          lineHeight: 1.7,
          color: '#172b4d',

          // Headings
          '& h1': { fontSize: '24px', fontWeight: 600, mt: 3, mb: 1.5, color: '#172b4d', borderBottom: '1px solid #e5e7eb', pb: 1 },
          '& h2': { fontSize: '20px', fontWeight: 600, mt: 3, mb: 1, color: '#172b4d' },
          '& h3': { fontSize: '16px', fontWeight: 600, mt: 2.5, mb: 1, color: '#172b4d' },
          '& h4, & h5, & h6': { fontWeight: 600, mt: 2, mb: 0.5, color: '#172b4d' },

          // Paragraphs & text
          '& p': { mb: 1.5, mt: 0 },
          '& strong, & b': { fontWeight: 600 },
          '& em, & i': { fontStyle: 'italic' },

          // Links
          '& a': { color: '#0052cc', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },

          // Lists
          '& ul': { pl: 3, mb: 1.5, listStyleType: 'disc' },
          '& ol': { pl: 3, mb: 1.5, listStyleType: 'decimal' },
          '& li': { mb: 0.5 },
          '& li > ul, & li > ol': { mt: 0.5, mb: 0 },

          // Code
          '& code': {
            fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '12px',
            bgcolor: '#f4f5f7',
            border: '1px solid #dfe1e6',
            borderRadius: '3px',
            px: '4px',
            py: '2px',
            color: '#172b4d',
          },
          '& pre': {
            bgcolor: '#f4f5f7',
            border: '1px solid #dfe1e6',
            borderRadius: '3px',
            p: 2,
            mb: 2,
            overflowX: 'auto',
            '& code': { bgcolor: 'transparent', border: 'none', p: 0, fontSize: '13px' },
          },

          // Blockquote
          '& blockquote': {
            borderLeft: '3px solid #dfe1e6',
            pl: 2,
            ml: 0,
            color: '#5e6c84',
            fontStyle: 'italic',
          },

          // Tables
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            mb: 2,
            fontSize: '14px',
          },
          '& th': {
            bgcolor: '#f4f5f7',
            border: '1px solid #dfe1e6',
            px: 1.5,
            py: 1,
            fontWeight: 600,
            textAlign: 'left',
            color: '#172b4d',
          },
          '& td': {
            border: '1px solid #dfe1e6',
            px: 1.5,
            py: 1,
            verticalAlign: 'top',
          },
          '& tr:nth-of-type(even)': { bgcolor: '#fafbfc' },

          // Confluence info/note/warning panels
          '& .confluence-information-macro': {
            borderRadius: '3px',
            p: 2,
            mb: 2,
            display: 'flex',
            gap: 1.5,
          },
          '& .confluence-information-macro-note': { bgcolor: '#fffae6', border: '1px solid #ffe380' },
          '& .confluence-information-macro-information': { bgcolor: '#deebff', border: '1px solid #4c9aff' },
          '& .confluence-information-macro-warning': { bgcolor: '#ffebe6', border: '1px solid #ff8f73' },
          '& .confluence-information-macro-tip': { bgcolor: '#e3fcef', border: '1px solid #57d9a3' },

          // Images
          '& img': { maxWidth: '100%', height: 'auto', borderRadius: '3px' },

          // Horizontal rule
          '& hr': { border: 'none', borderTop: '1px solid #e5e7eb', my: 2 },

          // Expand macro
          '& .expand-container': { border: '1px solid #dfe1e6', borderRadius: '3px', mb: 1.5 },
          '& .expand-control': { bgcolor: '#f4f5f7', px: 2, py: 1, cursor: 'pointer', fontWeight: 600 },
          '& .expand-content': { p: 2 },
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </Box>
  );
};
